import { supabase } from './supabase';

type AuthRouteContextOptions = {
  searchParams: URLSearchParams;
  hash?: string;
  requiredTypeValues?: string[];
  queryKeys?: string[];
  hashKeys?: string[];
  includeSupabaseAuthReferrer?: boolean;
};

export type AuthRouteContext = {
  hasContext: boolean;
  hashParams: URLSearchParams;
  queryType: string | null;
  cameFromSupabaseAuth: boolean;
};

export type AuthRouteContextPreset = {
  requiredTypeValues?: string[];
  queryKeys?: string[];
  hashKeys?: string[];
  includeSupabaseAuthReferrer?: boolean;
};

export const AUTH_ROUTE_CONTEXT_PRESETS = {
  passwordRecovery: {
    requiredTypeValues: ['recovery'],
    queryKeys: ['token_hash', 'code', 'access_token'],
    hashKeys: ['access_token'],
  },
  authCallback: {
    queryKeys: ['code', 'access_token', 'refresh_token', 'token_hash', 'type', 'error'],
    hashKeys: ['access_token', 'refresh_token', 'error'],
  },
  emailVerification: {
    requiredTypeValues: ['signup'],
    queryKeys: ['token', 'token_hash', 'code', 'email', 'error'],
    hashKeys: ['access_token', 'error'],
  },
} satisfies Record<string, AuthRouteContextPreset>;

const AUTH_HINT_STORAGE_KEYS = ['tension_access_token', 'tension_token_expired'] as const;

function getReferrer(): string {
  if (typeof document === 'undefined') return '';
  return document.referrer || '';
}

function getHashValue(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hash || '';
}

export function resolveAuthRouteContext(options: AuthRouteContextOptions): AuthRouteContext {
  const {
    searchParams,
    hash = getHashValue(),
    requiredTypeValues = [],
    queryKeys = [],
    hashKeys = [],
    includeSupabaseAuthReferrer = true,
  } = options;

  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
  const queryType = searchParams.get('type');
  const cameFromSupabaseAuth =
    includeSupabaseAuthReferrer && getReferrer().includes('.supabase.co/auth/v1/');

  const hasRequiredType =
    requiredTypeValues.length > 0 &&
    (requiredTypeValues.includes(queryType ?? '') ||
      requiredTypeValues.includes(hashParams.get('type') ?? ''));

  const hasQueryKey = queryKeys.some((key) => Boolean(searchParams.get(key)));
  const hasHashKey = hashKeys.some((key) => Boolean(hashParams.get(key)));

  return {
    hasContext: cameFromSupabaseAuth || hasRequiredType || hasQueryKey || hasHashKey,
    hashParams,
    queryType,
    cameFromSupabaseAuth,
  };
}

export function resolveAuthRouteContextFromPreset(
  searchParams: URLSearchParams,
  preset: AuthRouteContextPreset,
  hash?: string,
): AuthRouteContext {
  return resolveAuthRouteContext({
    searchParams,
    hash,
    requiredTypeValues: preset.requiredTypeValues,
    queryKeys: preset.queryKeys,
    hashKeys: preset.hashKeys,
    includeSupabaseAuthReferrer: preset.includeSupabaseAuthReferrer,
  });
}

export function clearAuthSessionHints(): void {
  if (typeof window === 'undefined') return;
  try {
    for (const key of AUTH_HINT_STORAGE_KEYS) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage access restrictions.
  }
}

export async function enforcePublicRouteIsolation(hasAuthContext: boolean): Promise<void> {
  if (hasAuthContext) return;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) return;
  await supabase.auth.signOut();
  clearAuthSessionHints();
}
